import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  is_registered_user: any;

  // Reactive state management
  private isLoggedInSubject = new BehaviorSubject<boolean>(this.hasToken());
  private userDataSubject = new BehaviorSubject<any>(this.getUserData());

  // Public observables
  public isLoggedIn$ = this.isLoggedInSubject.asObservable();
  public userData$ = this.userDataSubject.asObservable();

  constructor(private apiService: ApiService) {}

  // Get current values
  get isLoggedIn(): boolean {
    return this.isLoggedInSubject.value;
  }

  get userData(): any {
    return this.userDataSubject.value;
  }

  // Update authentication state
  setLoggedInState(isLoggedIn: boolean, userData?: any) {
    this.isLoggedInSubject.next(isLoggedIn);
    if (!isLoggedIn) {
      this.userDataSubject.next(null);
      return;
    }
    if (userData) {
      this.userDataSubject.next(userData);
    }
  }

  // Check if user has valid token
  private hasToken(): boolean {
    try {
      const userData = localStorage.getItem('userData1');
      if (!userData) {
        return false;
      }
      const parsed = JSON.parse(userData);
      return !!(parsed?.id || parsed?.user_code);
    } catch (error) {
      return false;
    }
  }

  // Get user data from localStorage
  private getUserData(): any {
    try {
      const userData = localStorage.getItem('userData1');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      return null;
    }
  }

  // Initialize auth state (call this on app startup)
  initializeAuthState() {
    // Use setTimeout to defer state update until after current change detection cycle
    setTimeout(() => {
      const isLoggedIn = this.hasToken();
      const userData = this.getUserData();
      this.setLoggedInState(isLoggedIn, userData);
    }, 0);
  }

  // Force refresh auth state (useful after login/registration)
  refreshAuthState() {
    const isLoggedIn = this.hasToken();
    const userData = this.getUserData();
    this.setLoggedInState(isLoggedIn, userData);
  }

  // Login method
  login(userData: any) {
    localStorage.setItem('userData1', JSON.stringify(userData));
    localStorage.setItem('userData', userData);
    localStorage.setItem('useractive', userData?.is_active);

    // Publish before callers close the login modal so the next Book Now click
    // sees the current session instead of the pre-login snapshot.
    this.setLoggedInState(true, userData);
  }

  // Logout method
  logout() {
    localStorage.removeItem('userData1');
    localStorage.removeItem('userData');
    localStorage.removeItem('useractive');
    localStorage.removeItem('registrationPhoneNumber');
    localStorage.removeItem('registrationOtp');
    this.setLoggedInState(false, null);
  }

  validatePhoneNumber(phoneNumber: string): boolean {
    // Indian mobile number validation (10 digits starting with 6-9)
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(phoneNumber);
  }

  sendOtp(phoneNumber: string, turnstileToken?: string): Observable<any> {
    if (!this.validatePhoneNumber(phoneNumber)) {
      return throwError(() => new Error('Invalid phone number format'));
    }

    const payload: any = {
      phone_number: phoneNumber,
      // country_code: '+91' // Can be made dynamic if needed
    };

    if (turnstileToken != 'is_resend_otp') {
      payload.turnstile_token = turnstileToken;
    } else if (turnstileToken === 'is_resend_otp') {
      payload.is_resend_otp = true;
    }

    return this.apiService.sendOtp(payload).pipe(
      map((response) => {
        // Handle successful response
        if (response && response.success) {
          this.is_registered_user = response?.data?.is_registered_user;

          return response;
        }
        // Handle error in response
        throw new Error(response?.message || 'Failed to send OTP');
      }),
      catchError((error) => {
        console.error('OTP send error:', error);
        if (error?.error?.message) {
          return throwError(() => new Error(error.error.message));
        }
        return throwError(
          () => new Error('Failed to send OTP. Please try again.'),
        );
      }),
    );
  }

  verifyOtp(
    phoneNumber: string,
    otp: string,
    is_guest: boolean = false,
  ): Observable<any> {
    if (!this.validatePhoneNumber(phoneNumber)) {
      return throwError(() => new Error('Invalid phone number format'));
    }

    if (!otp || otp.length !== 6 || !/^\d+$/.test(otp)) {
      return throwError(() => new Error('Invalid OTP format'));
    }

    const payload: any = {
      phone_number: phoneNumber,
      otp: otp,
      action_type:
        this.is_registered_user === true ? 'Registered User' : 'New User',
    };

    if (this.is_registered_user === false && is_guest) {
      payload.is_guest_user = true;
    }

    return this.apiService.verifyOtp(payload).pipe(
      map((response) => {
        if (response && response.success) {
          return response;
        }
        throw new Error(response?.message || 'Failed to verify OTP');
      }),
      catchError((error) => {
        const errorMsg =
          error?.error?.message ||
          error?.message ||
          'Failed to verify OTP. Please try again.';
        return throwError(() => new Error(errorMsg));
      }),
    );
  }

  register(data: {
    phone_number: string;
    first_name: string;
    last_name: string;
    email_id: string;
    // otp_number: string;
    user_type: string;
  }): Observable<any> {
    return this.apiService.userRegistration(data).pipe(
      map((response) => {
        if (response && response.success) {
          return response;
        }
        throw new Error(response?.message || 'Registration failed');
      }),
      catchError((error) => {
        console.error('Registration error:', error);
        if (error?.error?.message) {
          return throwError(() => new Error(error.error.message));
        }
        return throwError(
          () => new Error('Registration failed. Please try again.'),
        );
      }),
    );
  }
}
